/**
 * CreateGroupRequestDialog Component
 * Dialog for standard users to request group creation
 * (Moderators/Admins create groups directly via CreateGroupDialog)
 */

import {
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from '../ui/BaseDialog';
import { useCreateGroupRequest, useEligibleApprovers } from '../../hooks/api/useGroupRequests';
import { useUsers } from '../../hooks/api/useUsers';
import { GroupType, GroupVisibility, User } from '../../api/data-contracts';
import AuthService from '../../services/AuthService';

interface CreateGroupRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateGroupRequestForm {
  group_name: string;
  description: string;
  justification: string;
  group_type: GroupType;
  visibility: GroupVisibility;
  approver_ids: string[];
  initial_admin_id: string;
}

export const CreateGroupRequestDialog: React.FC<CreateGroupRequestDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const createRequestMutation = useCreateGroupRequest();
  const currentUser = AuthService.getUser();

  // Fetch eligible approvers (admins/moderators)
  const { data: approversResponse, isLoading: approversLoading } = useEligibleApprovers();
  const eligibleApprovers = approversResponse?.approvers || [];

  // Fetch all users for initial admin selection
  const { data: usersData, isLoading: usersLoading } = useUsers({ skip: 0, limit: 1000 });
  const allUsers = usersData?.users || [];

  const [form, setForm] = useState<CreateGroupRequestForm>({
    group_name: '',
    description: '',
    justification: '',
    group_type: GroupType.Team,
    visibility: GroupVisibility.Organization,
    approver_ids: [],
    initial_admin_id: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        group_name: '',
        description: '',
        justification: '',
        group_type: 'team' as GroupType,
        visibility: 'organization' as GroupVisibility,
        approver_ids: [],
        initial_admin_id: '',
      });
      setErrors({});
      createRequestMutation.reset();
    }
  }, [open]);

  const handleChange = (field: keyof CreateGroupRequestForm, value: any) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Group name validation
    if (!form.group_name.trim()) {
      newErrors.group_name = t('groupRequests.create.validation.groupNameRequired');
    } else if (form.group_name.length < 3) {
      newErrors.group_name = t('groupRequests.create.validation.groupNameTooShort');
    } else if (form.group_name.length > 100) {
      newErrors.group_name = t('groupRequests.create.validation.groupNameTooLong');
    }

    // Justification validation
    if (!form.justification.trim()) {
      newErrors.justification = t('groupRequests.create.validation.justificationRequired');
    } else if (form.justification.length < 20) {
      newErrors.justification = t('groupRequests.create.validation.justificationTooShort');
    }

    // Approver validation
    if (form.approver_ids.length === 0) {
      newErrors.approver_ids = t('groupRequests.create.validation.approverRequired');
    }

    // Check requester is not in approvers
    if (currentUser && form.approver_ids.includes(currentUser.user_id)) {
      newErrors.approver_ids = t('groupRequests.create.validation.requesterCannotBeApprover');
    }

    // Initial admin validation
    if (!form.initial_admin_id) {
      newErrors.initial_admin_id = t('groupRequests.create.validation.initialAdminRequired');
    }

    // Check requester is not the initial admin
    if (currentUser && form.initial_admin_id === currentUser.user_id) {
      newErrors.initial_admin_id = t('groupRequests.create.validation.requesterCannotBeInitialAdmin');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    createRequestMutation.mutate(form, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
    });
  };

  // Filter out current user from approvers and initial admin lists
  const availableApprovers = currentUser
    ? eligibleApprovers.filter((u) => u.id !== currentUser.user_id)
    : eligibleApprovers;

  const availableInitialAdmins = currentUser
    ? allUsers.filter((u) => u.id !== currentUser.user_id)
    : allUsers;

  const groupTypes: GroupType[] = [
    GroupType.Team,
    GroupType.Department,
    GroupType.Project,
    GroupType.Topic,
    GroupType.Role,
    GroupType.Functional,
    GroupType.Custom
  ];
  const visibilityOptions: GroupVisibility[] = [
    GroupVisibility.Private,
    GroupVisibility.Organization,
    GroupVisibility.Public
  ];

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t('groupRequests.create.title')}
      maxWidth="md"
      fullWidth
      loading={createRequestMutation.isPending}
      error={
        createRequestMutation.error instanceof Error
          ? createRequestMutation.error.message
          : createRequestMutation.error
          ? t('groupRequests.create.error')
          : null
      }
      actions={
        <>
          <Button onClick={onClose}>{t('groupRequests.create.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<SendIcon />}>
            {t('groupRequests.create.submit')}
          </Button>
        </>
      }
    >
      <Box sx={{ pt: 1 }}>
        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">{t('groupRequests.create.infoMessage')}</Typography>
        </Alert>

        {/* Group Name */}
        <TextField
          autoFocus
          fullWidth
          required
          label={t('groupRequests.create.groupName')}
          value={form.group_name}
          onChange={(e) => handleChange('group_name', e.target.value)}
          placeholder={t('groupRequests.create.groupNamePlaceholder')}
          helperText={errors.group_name || t('groupRequests.create.groupNameHelper')}
          error={Boolean(errors.group_name)}
          sx={{ mb: 3 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label={t('groupRequests.create.description')}
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={t('groupRequests.create.descriptionPlaceholder')}
          multiline
          rows={2}
          helperText={t('groupRequests.create.descriptionHelper')}
          sx={{ mb: 3 }}
        />

        {/* Justification */}
        <TextField
          fullWidth
          required
          label={t('groupRequests.create.justification')}
          value={form.justification}
          onChange={(e) => handleChange('justification', e.target.value)}
          placeholder={t('groupRequests.create.justificationPlaceholder')}
          multiline
          rows={3}
          helperText={errors.justification || t('groupRequests.create.justificationHelper')}
          error={Boolean(errors.justification)}
          sx={{ mb: 3 }}
        />

        <Box display="flex" gap={2} mb={3}>
          {/* Group Type */}
          <FormControl fullWidth required error={Boolean(errors.group_type)}>
            <InputLabel>{t('groupRequests.create.groupType')}</InputLabel>
            <Select
              value={form.group_type}
              label={t('groupRequests.create.groupType')}
              onChange={(e) => handleChange('group_type', e.target.value as GroupType)}
            >
              {groupTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`groupRequests.groupTypes.${type}`)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t('groupRequests.create.groupTypeHelper')}</FormHelperText>
          </FormControl>

          {/* Visibility */}
          <FormControl fullWidth required error={Boolean(errors.visibility)}>
            <InputLabel>{t('groupRequests.create.visibility')}</InputLabel>
            <Select
              value={form.visibility}
              label={t('groupRequests.create.visibility')}
              onChange={(e) => handleChange('visibility', e.target.value as GroupVisibility)}
            >
              {visibilityOptions.map((vis) => (
                <MenuItem key={vis} value={vis}>
                  {t(`groupRequests.visibility.${vis}`)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t('groupRequests.create.visibilityHelper')}</FormHelperText>
          </FormControl>
        </Box>

        {/* Approvers */}
        <Autocomplete
          multiple
          fullWidth
          options={availableApprovers}
          getOptionLabel={(option) => {
            const displayName = option.full_name || option.username;
            // Only show email in parentheses if it's different from the display name
            return displayName !== option.email ? `${displayName} (${option.email})` : displayName;
          }}
          value={availableApprovers.filter((u) => form.approver_ids.includes(u.id))}
          onChange={(_, newValue) => handleChange('approver_ids', newValue.map((u) => u.id))}
          loading={approversLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label={t('groupRequests.create.approvers')}
              placeholder={t('groupRequests.create.approversPlaceholder')}
              helperText={errors.approver_ids || t('groupRequests.create.approversHelper')}
              error={Boolean(errors.approver_ids)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {approversLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.full_name || option.username}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          sx={{ mb: 3 }}
        />

        {/* Initial Admin */}
        <Autocomplete
          fullWidth
          options={availableInitialAdmins}
          getOptionLabel={(option) => {
            const displayName = option.full_name || option.username;
            // Only show email in parentheses if it's different from the display name
            return displayName !== option.email ? `${displayName} (${option.email})` : displayName;
          }}
          value={availableInitialAdmins.find((u) => u.id === form.initial_admin_id) || null}
          onChange={(_, newValue) => handleChange('initial_admin_id', newValue?.id || '')}
          loading={usersLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label={t('groupRequests.create.initialAdmin')}
              placeholder={t('groupRequests.create.initialAdminPlaceholder')}
              helperText={errors.initial_admin_id || t('groupRequests.create.initialAdminHelper')}
              error={Boolean(errors.initial_admin_id)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />
      </Box>
    </BaseDialog>
  );
};
